import { OtpEsecMotiv } from '../../../api/generated/types.gen';
import { TTL_COD_MINUTE } from './otp-cod';

/**
 * Textele ecranelor de cod unic, alese dupa MOTIVUL spus de server. Pur, ca `otp-cod.ts`.
 *
 * 🔴 De ce exista (masurat, 2026-10-07): oamenii cer un al doilea cod si il scriu pe primul care
 * ajunge; intre 13% si 30% din primele incercari de dupa o recerere picau. Doua lucruri s-au
 * schimbat in backend si se spun aici:
 *   1. ORICE cod al omului, neexpirat si nefolosit, e acceptat — nu doar cel mai recent. Deci
 *      niciun text nu mai are voie sa ceara „cel mai recent cod": ar fi fals.
 *   2. `/otp/verify` spune de ce a refuzat (`motiv`), deci „a expirat" se afirma DOAR cand o spune
 *      serverul. `COD_EXPIRAT` / `COD_FOLOSIT` vin numai pentru un cod chiar emis pentru contul
 *      acela; orice altceva (cod gresit, cont neeligibil, prea multe incercari) e `COD_INVALID`.
 *
 * ⚠️ Textele numesc butonul EXACT cum scrie pe ecran: «Trimite alt cod» (`VerificareCodPage`).
 * Un text care trimite la un buton cu alt nume lasa omul sa-l caute.
 */

/** Numele butonului de recerere, intr-un singur loc: il citesc si textele, si ecranul. */
export const ETICHETA_COD_NOU = 'Trimite alt cod';

export interface MesajEsecVerificare {
    text: string;
    /** Serverul a spus ca DOAR un cod nou mai ajuta (expirat sau deja folosit). */
    cereCodNou: boolean;
}

/**
 * Motivul, citit defensiv: campul vine de pe sarma ca sir oarecare. Orice valoare necunoscuta —
 * inclusiv lipsa, adica un backend dinaintea schimbarii — e `COD_INVALID`, singurul text care nu
 * afirma nimic ce n-a spus serverul.
 */
export function motivVerificare(motiv: unknown): OtpEsecMotiv {
    if (motiv === OtpEsecMotiv.COD_EXPIRAT) return OtpEsecMotiv.COD_EXPIRAT;
    if (motiv === OtpEsecMotiv.COD_FOLOSIT) return OtpEsecMotiv.COD_FOLOSIT;
    return OtpEsecMotiv.COD_INVALID;
}

/**
 * Ce vede omul dupa un 400 de la `/otp/verify`.
 *
 * `esecuri` = cate incercari au picat la rand pe ecranul asta, cu tot cu cea de acum. Abia de la
 * a doua se sugereaza un cod nou: la prima, cel mai probabil e o cifra gresita, iar un indemn la
 * recerere ar produce exact valul de coduri pe care il masuram.
 */
export function mesajEsecVerificare(motiv: unknown, esecuri: number): MesajEsecVerificare {
    switch (motivVerificare(motiv)) {
        case OtpEsecMotiv.COD_EXPIRAT:
            return {
                text: 'Codul acesta a expirat. Apasă «' + ETICHETA_COD_NOU + '» și scrie-l pe cel pe care îl primești.',
                cereCodNou: true,
            };
        case OtpEsecMotiv.COD_FOLOSIT:
            return {
                text: 'Codul acesta a fost deja folosit. Apasă «' + ETICHETA_COD_NOU + '» ca să primești unul nou.',
                cereCodNou: true,
            };
        default: {
            const baza = 'Codul nu se potrivește. Verifică cifrele: merge orice cod primit în ultimele '
                + TTL_COD_MINUTE + ' minute.';
            return {
                text: esecuri >= 2 ? baza + ' Dacă tot nu merge, apasă «' + ETICHETA_COD_NOU + '».' : baza,
                cereCodNou: false,
            };
        }
    }
}

/**
 * „un minut" / „12 minute" / „20 de minute", rotunjit in SUS (ca `formateazaSecunde`: la 61 de
 * secunde inca mai e de asteptat peste un minut). `null` cand serverul n-a spus cat.
 */
export function formateazaMinute(secunde: number | null | undefined): string | null {
    if (typeof secunde !== 'number' || !Number.isFinite(secunde) || secunde < 0) return null;
    const minute = Math.max(1, Math.ceil(secunde / 60));
    if (minute === 1) return 'un minut';
    // Regula de numeral: „de" de la 20 in sus, mai putin cand ultimele doua cifre sunt 01–19.
    const rest = minute % 100;
    return minute + (minute >= 20 && (rest === 0 || rest >= 20) ? ' de minute' : ' minute');
}

/**
 * Mesajul de pe ecranul de introducere a codului cand serverul NU a mai trimis un cod (429
 * `LIMITA_COD`): contul si-a primit numarul maxim de coduri, dar cele primite raman bune.
 *
 * 🔴 Nu afirma nicaieri ca a plecat ceva. E info, nu eroare: omul are deja in mesaje un cod care
 * merge, si singurul lucru de facut e sa-l scrie.
 */
export function mesajLimitaCod(secundePanaLaCodNou: number | null | undefined): string {
    const peste = formateazaMinute(secundePanaLaCodNou);
    return 'Nu ți-am trimis un cod nou: ai cerut deja mai multe. Scrie oricare cod primit în ultimele '
        + TTL_COD_MINUTE + ' minute. '
        + (peste ? 'Poți cere alt cod peste ' + peste + '.' : 'Poți cere alt cod ceva mai târziu.');
}

/** 503 `TRIMITERE_ESUATA`: contul exista, dar mesajul n-a putut pleca. Nu s-a trimis nimic. */
export const MESAJ_TRIMITERE_ESUATA = 'Nu am putut trimite codul. Încearcă din nou peste un minut.';
